import React from 'react';
import { Box, Card, CardContent, Typography, Avatar, Chip } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import {
  Note as NoteIcon,
  Comment as CommentIcon,
  AutoStories as MemoryIcon
} from '@mui/icons-material';
import type { ActivityItem as ActivityItemType } from './useUnifiedActivity';
import { MetadataDisplay } from '../components/MetadataDisplay';
import MentionDisplay from '../../../../components/common/MentionDisplay';

interface ActivityItemProps {
  item: ActivityItemType;
  showMetadata?: boolean;
  metadataFormat?: 'short' | 'long';
}

/**
 * ActivityItem - Renders individual activity with type-specific styling
 */
export const ActivityItem: React.FC<ActivityItemProps> = ({
  item,
  showMetadata = false,
  metadataFormat = 'short'
}) => {
  const getTypeIcon = () => {
    switch (item.type) {
      case 'note':
        return <NoteIcon />;
      case 'comment':
        return <CommentIcon />;
      case 'memory':
        return <MemoryIcon />;
    }
  };

  const getTypeColor = (): 'primary' | 'secondary' | 'success' => {
    switch (item.type) {
      case 'note':
        return 'primary';
      case 'comment':
        return 'secondary';
      case 'memory':
        return 'success';
    }
  };

  const initials = item.authorName
    .split(' ')
    .map(n => n.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Avatar */}
          <Avatar sx={{ bgcolor: `${getTypeColor()}.main` }}>
            {initials}
          </Avatar>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {item.authorName}
              </Typography>

              <Chip
                icon={getTypeIcon()}
                label={item.type}
                size="small"
                color={getTypeColor()}
                sx={{ textTransform: 'capitalize' }}
              />

              <Typography variant="caption" color="text.secondary">
                {formatDistanceToNow(item.createdAt, { addSuffix: true })}
              </Typography>

              {item.updatedAt && item.updatedAt.getTime() !== item.createdAt.getTime() && (
                <Typography variant="caption" color="text.secondary">
                  (edited)
                </Typography>
              )}
            </Box>

            {/* Content */}
            <MentionDisplay
              content={item.content}
              mentions={item.data.mentions || []}
              sx={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '0.875rem',
                lineHeight: 1.43
              }}
              component="div"
            />

            {/* Memory-specific: Title */}
            {item.type === 'memory' && 'title' in item.data && item.data.title && (
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                {item.data.title}
              </Typography>
            )}

            {/* Note-specific: Private indicator */}
            {item.type === 'note' && 'isPrivate' in item.data && item.data.isPrivate && (
              <Chip label="Private" size="small" sx={{ mt: 1 }} />
            )}

            {/* Metadata */}
            {showMetadata && item.updatedAt && (
              <Box sx={{ mt: 1 }}>
                <MetadataDisplay
                  updatedAt={item.updatedAt}
                  updatedByName={item.authorName}
                  displayFormat={metadataFormat}
                />
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
